import { Component, Input, isDevMode, OnInit } from '@angular/core';

import { AlertController } from '@ionic/angular';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { EmailComposer } from '@awesome-cordova-plugins/email-composer/ngx';
import { TranslateService } from '@jht/brand-translate';

import { mediaDirectory, NavItem } from '../../../models';
import { EmailImageService } from '../../../services/email/email-image.service';
import { FileService } from '../../../services/file.service';
import { AnalyticsService } from '../../../services/analytics.service';
import { FileType } from '../../file/filemanager';
import { NetworkStatusService } from '../../../services/network-status.service';
import { PlatformService } from '../../../services/platform.service';
import { base64ToEmailComposerBase64Format, TEN_MEGABYTES } from '../../../helpers/utils';

@Component({
    selector: 'mea-navigation-card',
    templateUrl: 'navigation-card.component.html',
    styleUrls: ['./navigation-card.component.scss'],
    providers: [
        { provide: Window, useValue: window },
        EmailComposer,
        FileOpener,
    ],
    standalone: false
})
export class NavigationCardComponent implements OnInit {
    @Input() cardClass: string;
    @Input() cardData: any;

    cardImage: string;
    cardRoute: string;
    cardSubtitle: string;
    cardTitle: string;
    cardType: FileType;

    isExternalLink: boolean;
    isBrochure: boolean;
    isInternalNavigation: boolean;

    constructor(
        private alertController: AlertController,
        private emailImageService: EmailImageService,
        private fileService: FileService,
        private platformService: PlatformService,
        private networkStatusService: NetworkStatusService,
        private window: Window,
        private fileOpener: FileOpener,
        private translateService: TranslateService,
        private analytics: AnalyticsService,
    ) { }

    ngOnInit(): void {
        if (this.cardData) {
            this.prepareCardData(this.cardData);
        }
    }

    checkNetwork(route: string): void {
        if (this.platformService.isBrowser) {
            this.handleExternalRoute(route);
        } else {
            this.networkStatusService.requireConnectionFactory(
                this.handleExternalRoute.bind(this)
            )(route);
        }
    }

    handleExternalRoute(route: string): void {
        this.analytics.track({
            type: 'web-link',
            data: { link: route }
        });

        this.window.open(route);
    }

    async openPdfPrompt(): Promise<void> {
        const pdf: string = this.cardData['brochure-file'];

        const alert = await this.alertController.create({
            header: this.translateService.instant('BROCHURE-MODAL_TITLE_TEXT'),
            message: this.translateService.instant('BROCHURE-MODAL_COPY_TEXT'),
            buttons: [
                {
                    text: this.translateService.instant('BROCHURE-MODAL_EMAIL_TEXT'),
                    role: 'confirm',
                    handler: async () => {
                        this.analytics.track({
                            type: 'email',
                            tags: ['brochure'],
                            data: {
                                file: pdf.split('/').pop()
                            }
                        });

                        try {
                            const attachments = [];
                            if (this.platformService.isNativeDevice) {
                                try {
                                    const pdfName = pdf.split('/').pop();

                                    const fileSize = await Filesystem.stat({
                                        path: [mediaDirectory.localMedia, encodeURIComponent(pdf)].filter(Boolean).join('/'),
                                        directory: Directory.Data,
                                    }).then(file => file.size);

                                    if (fileSize <= TEN_MEGABYTES) {
                                        const emailFileData = await this.fileService.loadImageDataUrl(pdf)
                                            .then((b64) => base64ToEmailComposerBase64Format(b64, pdfName));

                                        attachments.push(emailFileData);
                                    }
                                } catch (e) {
                                    if (isDevMode()) {
                                        console.error('Could not attach brochure PDF, sending email without attachment: ', e);
                                    }
                                }
                            }

                            this.emailImageService.openEmailClient({
                                composerOptions: {
                                    subject: this.cardTitle,
                                    body: this.emailImageService.getDefaultBody(
                                        pdf
                                    ),
                                    attachments
                                },
                            });
                        } catch (e) {
                            if (isDevMode()) {
                                console.error(e);
                            }
                        }
                    },
                },
                {
                    text: this.translateService.instant('BROCHURE-MODAL_PREVIEW_TEXT'),
                    role: 'confirm',
                    handler: async () => {
                        this.analytics.track({
                            type: 'preview',
                            tags: ['brochure'],
                            data: {
                                file: pdf.split('/').pop(),
                            }
                        });

                        if (this.platformService.isBrowser) {
                            this.window.open(
                                mediaDirectory.externalMedia + pdf
                            );
                            return false;
                        } else {
                            const relativeFilePath = await this.fileService.getFilePath(pdf, 'media');

                            const file = {
                                path: relativeFilePath,
                                directory: Directory.Data,
                            };

                            const filePath = await Filesystem.getUri(file).then(fileData => fileData.uri);
                            this.fileOpener
                                .open(filePath, 'application/pdf')
                                .catch((e) => null);
                        }
                    },
                },
            ],
        });

        await alert.present();
    }

    prepareCardData(data: NavItem): void {
        this.isBrochure = data.hasOwnProperty('brochure-file');
        this.isExternalLink = !!data.url;
        this.isInternalNavigation = !this.isBrochure && !this.isExternalLink;
        this.cardRoute = this.getRoute(data);
        this.cardImage = data.icon ? data.icon.icon : data['hero-image'];
        this.cardType = data.icon
            ? ('icon' as FileType)
            : (data.type as FileType);
        this.cardTitle = this.translateService.instant(data['title-translated'] || data.title);
        this.cardSubtitle = data.subtitle;
    }

    private getRoute(data: NavItem): string {
        if (data.type === 'category') {
            return `/${data.route}`;
        }

        if (data.url) {
            return data.url;
        }

        return data.route;
    }
}
